import { airlineName } from "../airline-names";
import { escapeHtml } from "../escape";
import { findFlight } from "../find-flight";
import type { PresentationSurface } from "../presentation";
import { $flights, $planes, $selectedId, $signal, $theme, selectPlane } from "../store";
import type { FlightRecord, LivePlane } from "../types";

export function renderMobile(host: HTMLElement, surface: PresentationSurface = "standalone") {
  const isEmbeddedApp = surface === "app";
  const standaloneChrome = isEmbeddedApp
    ? ""
    : `
      <div class="m-header"></div>

      <div class="m-top">
        <div class="m-brand"><i class="ph-fill ph-airplane-tilt"></i>Octa Feeder</div>
        <div class="m-pill"><span class="dot"></span><span id="mOnline">ONLINE</span></div>
        <div class="m-pill"><i class="ph ph-cell-signal-full"></i><span id="mDb">47.4dB</span></div>
        <button type="button" class="m-pill btn" id="mThemeBtn" aria-label="Toggle theme">
          <i class="ph ph-moon" id="mThemeIcon" aria-hidden="true"></i>
        </button>
      </div>`;
  const settingsTab = isEmbeddedApp
    ? ""
    : `<button type="button" class="m-tab" id="mSettingsTab" role="tab" data-tab="settings"
          aria-controls="mTabSettings" aria-selected="false" tabindex="-1">
          <i class="ph ph-gear" aria-hidden="true"></i>Settings
        </button>`;
  const settingsPanel = isEmbeddedApp
    ? ""
    : `<section class="m-tabcontent" id="mTabSettings" role="tabpanel" aria-labelledby="mSettingsTab" hidden>
          <div style="padding:16px;color:var(--muted);font-size:var(--fs-xs);line-height:1.6">
            <h3 style="font-size:var(--fs-2xs);color:var(--muted);text-transform:uppercase;letter-spacing:.5px;margin:0 0 10px">Theme</h3>
            <div style="display:flex;gap:6px">
              <button class="m-pill" id="mThemeDark" style="height:34px;padding:0 12px"><i class="ph ph-moon"></i>Dark</button>
              <button class="m-pill" id="mThemeLight" style="height:34px;padding:0 12px"><i class="ph ph-sun"></i>Light</button>
            </div>
          </div>
        </section>`;

  host.innerHTML = `
    <div class="mobile-app" data-presentation-surface="${surface}">
      ${standaloneChrome}

      <div class="m-sheet" id="mSheet" role="region" aria-label="Map information">
        <button type="button" class="handle" id="mHandle" aria-controls="mSheet" aria-expanded="false"
          aria-label="Expand map information"><span aria-hidden="true"></span></button>
        <div class="m-tabs" role="tablist" aria-label="Map information">
          <button type="button" class="m-tab on" id="mAircraftTab" role="tab" data-tab="flights"
            aria-controls="mTabFlights" aria-selected="true">
            <i class="ph-fill ph-airplane" aria-hidden="true"></i>Aircraft<span class="badge" id="mCount" hidden></span>
          </button>
          <button type="button" class="m-tab" id="mAirportTab" role="tab" data-tab="airport"
            aria-controls="mTabAirport" aria-selected="false" tabindex="-1">
            <i class="ph ph-tower-control" aria-hidden="true"></i>Airport
          </button>
          ${settingsTab}
        </div>
        <section class="m-tabcontent" id="mTabFlights" role="tabpanel" aria-labelledby="mAircraftTab">
          <!-- When a plane is selected, the Web detail card mounts here
               (renderDetail(host) called from main.ts). It shows the same
               rich info as the desktop left rail: route, status badge,
               dep/arr times+gates, aircraft, airline, registration,
               operator, codeshare, etc. The list of all planes follows. -->
          <div id="mDetailHost" class="m-detail-host"></div>
          <div id="mFlightList"></div>
        </section>
        <section class="m-tabcontent" id="mTabAirport" role="tabpanel" aria-labelledby="mAirportTab" hidden>
          <!-- Same renderAirportPanel that powers the Web right rail —
               weather + runways + wind cards stack vertically. -->
          <div id="mAirportPanelHost" class="m-airport-host"></div>
        </section>
        ${settingsPanel}
      </div>
    </div>
  `;

  // Theme
  const mThemeBtn = host.querySelector<HTMLButtonElement>("#mThemeBtn");
  const mThemeIcon = host.querySelector<HTMLElement>("#mThemeIcon");
  function syncIcon() {
    if (mThemeIcon) mThemeIcon.className = $theme.get() === "dark" ? "ph ph-moon" : "ph ph-sun";
  }
  mThemeBtn?.addEventListener("click", () => {
    $theme.set($theme.get() === "dark" ? "light" : "dark");
    syncIcon();
  });
  host.querySelector<HTMLButtonElement>("#mThemeDark")?.addEventListener("click", () => {
    $theme.set("dark");
    syncIcon();
  });
  host.querySelector<HTMLButtonElement>("#mThemeLight")?.addEventListener("click", () => {
    $theme.set("light");
    syncIcon();
  });
  syncIcon();

  // Signal
  const onlineEl = host.querySelector<HTMLElement>("#mOnline");
  const dbEl = host.querySelector<HTMLElement>("#mDb");
  const countEl = host.querySelector("#mCount") as HTMLElement;
  $signal.subscribe((s) => {
    if (onlineEl) onlineEl.textContent = s.online ? "ONLINE" : "OFFLINE";
    if (dbEl) dbEl.textContent = `${s.dB.toFixed(1)}dB`;
    // Only show the badge once we have a real, non-zero count. The
    // store's default trackedCount is 0 and nanostores fires the
    // subscriber immediately on subscribe, so without this guard the
    // user sees a brief "Flights 0" flash before the first
    // /pifeeder/info poll lands and replaces it with the live number.
    if (s.trackedCount > 0) {
      countEl.textContent = String(s.trackedCount);
      countEl.hidden = false;
    } else {
      countEl.hidden = true;
    }
  });

  const sheet = host.querySelector("#mSheet") as HTMLElement;
  const handle = host.querySelector("#mHandle") as HTMLButtonElement;
  function isCompactLandscape() {
    return (
      isEmbeddedApp &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(orientation: landscape) and (max-height: 500px)").matches
    );
  }
  function setExpanded(expanded: boolean) {
    sheet.classList.toggle("expanded", expanded);
    handle.setAttribute("aria-expanded", String(expanded));
    handle.setAttribute("aria-label", expanded ? "Collapse map information" : "Expand map information");
    // The landscape contract changes the actual map container width. MapLibre
    // listens to window resize, so notify it after the CSS width transition.
    if (isCompactLandscape()) {
      window.setTimeout(() => window.dispatchEvent(new Event("resize")), 260);
    }
  }

  // When a plane is selected, expand the bottom sheet so the full detail
  // card (mounted at #mDetailHost by main.ts → renderDetail) is visible.
  $selectedId.subscribe(() => {
    if ($selectedId.get()) setExpanded(true);
  });

  // Flights list — renders into #mFlightList (the detail card lives in
  // #mDetailHost above it, mounted by main.ts via the shared renderDetail).
  const listEl = host.querySelector("#mFlightList") as HTMLElement;
  function row(p: LivePlane, flights: FlightRecord[]) {
    const active = $selectedId.get() === p.id;
    const flight = findFlight(p.call, flights);
    const route = flight
      ? `<span class="route-pill">${escapeHtml(flight.dep_iata ?? "—")}→${escapeHtml(flight.arr_iata ?? "—")}</span>`
      : "";
    const operator = airlineName(flight?.airline_icao) ?? flight?.airline_icao ?? p.operator ?? p.ctry ?? "";
    const accessibleLabel = [
      p.call,
      flight ? `${flight.dep_iata ?? "unknown origin"} to ${flight.arr_iata ?? "unknown destination"}` : "",
      operator,
      p.type ?? "",
      `Altitude ${p.alt.toLocaleString()} feet`,
      `Speed ${Math.round(p.spd)} knots`,
    ]
      .filter(Boolean)
      .join(", ");
    // p.id/call/type/operator and the route IATA codes are upstream-controlled
    // (ADS-B feed + airlabs lookup) — escape for attribute + body contexts.
    return `
      <button type="button" class="m-flightrow ${active ? "active" : ""}" data-id="${escapeHtml(p.id)}"
        aria-label="${escapeHtml(accessibleLabel)}" aria-pressed="${active}">
        <span class="ic"><i class="ph-fill ph-airplane" aria-hidden="true"></i></span>
        <span class="meta">
          <span class="call">${escapeHtml(p.call)}${route}</span>
          <span class="sub">${escapeHtml(operator)}${p.type ? " · " + escapeHtml(p.type) : ""}</span>
        </span>
        <span class="stat alt"><span class="k">ALT</span>${p.alt.toLocaleString()}</span>
        <span class="stat speed"><span class="k">SPD</span>${Math.round(p.spd)} kt</span>
      </button>`;
  }
  function renderList() {
    const planes = Object.values($planes.get()).sort((a, b) => a.call.localeCompare(b.call));
    const flights = $flights.get();
    listEl.innerHTML = planes.length
      ? planes.map((p) => row(p, flights)).join("")
      : `<div style="padding:24px;text-align:center;color:var(--muted)">No aircraft tracked.</div>`;
  }
  listEl.addEventListener("click", (e) => {
    const r = (e.target as HTMLElement).closest("button[data-id]") as HTMLButtonElement | null;
    if (!r) return;
    const id = r.dataset.id ?? null;
    const restoreFocus = document.activeElement === r;
    selectPlane(id);
    // Store subscriptions rebuild the list synchronously. Keep keyboard
    // users on the newly-rendered version of the same selected row.
    if (restoreFocus && id) {
      Array.from(listEl.querySelectorAll<HTMLButtonElement>("button[data-id]"))
        .find((button) => button.dataset.id === id)
        ?.focus();
    }
  });
  $planes.subscribe(renderList);
  $selectedId.subscribe(renderList);
  $flights.subscribe(renderList);

  // The Airport tab content is mounted by main.ts via renderAirportPanel
  // into #mAirportPanelHost, so we get the full Web right-rail experience
  // (Wx + Runways with mini-map + Wind compass) inside the bottom sheet.

  // Tab switching
  const tabs = Array.from(host.querySelectorAll<HTMLButtonElement>(".m-tab"));
  const panels: Record<string, HTMLElement> = {
    flights: host.querySelector("#mTabFlights")!,
    airport: host.querySelector("#mTabAirport")!,
  };
  const settingsPanelEl = host.querySelector<HTMLElement>("#mTabSettings");
  if (settingsPanelEl) panels.settings = settingsPanelEl;
  function activateTab(name: string) {
    for (const tab of tabs) {
      const active = tab.dataset.tab === name;
      tab.classList.toggle("on", active);
      tab.setAttribute("aria-selected", String(active));
      tab.tabIndex = active ? 0 : -1;
    }
    for (const [key, panel] of Object.entries(panels)) panel.hidden = key !== name;
  }
  for (const t of tabs) {
    t.addEventListener("click", () => activateTab(t.dataset.tab ?? "flights"));
    t.addEventListener("keydown", (event) => {
      const current = tabs.indexOf(t);
      let next = current;
      if (event.key === "ArrowRight") next = (current + 1) % tabs.length;
      else if (event.key === "ArrowLeft") next = (current - 1 + tabs.length) % tabs.length;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = tabs.length - 1;
      else return;
      event.preventDefault();
      const nextTab = tabs[next]!;
      activateTab(nextTab.dataset.tab ?? "flights");
      nextTab.focus();
    });
  }
  // Tapping an airport pin on the map should pop the user into the
  // Airport tab so they actually see the weather/runway data we just
  // loaded. The map.ts click handler dispatches this every tap (even
  // re-taps of the current airport — a $currentIcao subscription would
  // miss those because nanostores skip no-op sets).
  document.addEventListener("pifeeder:airport-tap", () => {
    activateTab("airport");
    setExpanded(true);
  });

  // Sheet handle: tap toggles expanded; drag also works
  let ignoreClickUntil = 0;
  handle.addEventListener("click", () => {
    // A completed touch drag can emit a synthetic click after touchend.
    // Ignore only that short window so the drag decision is not inverted.
    if (performance.now() < ignoreClickUntil) return;
    setExpanded(!sheet.classList.contains("expanded"));
  });
  let startPosition: number | null = null;
  let startSize: number | null = null;
  let dragLandscape = false;
  let didDrag = false;
  handle.addEventListener(
    "touchstart",
    (e) => {
      const touch = e.touches[0]!;
      dragLandscape = isCompactLandscape();
      startPosition = dragLandscape ? touch.clientX : touch.clientY;
      const bounds = sheet.getBoundingClientRect();
      startSize = dragLandscape ? bounds.width : bounds.height;
      didDrag = false;
      sheet.style.transition = "none";
    },
    { passive: true },
  );
  handle.addEventListener(
    "touchmove",
    (e) => {
      if (startPosition === null || startSize === null) return;
      const touch = e.touches[0]!;
      const position = dragLandscape ? touch.clientX : touch.clientY;
      const delta = startPosition - position;
      if (Math.abs(delta) >= 4) didDrag = true;
      if (dragLandscape) {
        const collapsedWidth = Math.min(window.innerWidth * 0.36, 420);
        const expandedWidth = Math.min(window.innerWidth * 0.4, 460);
        const newWidth = Math.min(expandedWidth, Math.max(collapsedWidth * 0.85, startSize + delta));
        sheet.style.width = newWidth + "px";
      } else {
        const newHeight = Math.min(window.innerHeight * 0.85, Math.max(150, startSize + delta));
        sheet.style.height = newHeight + "px";
      }
    },
    { passive: true },
  );
  handle.addEventListener("touchend", () => {
    const bounds = sheet.getBoundingClientRect();
    if (didDrag) {
      if (dragLandscape) {
        const collapsedWidth = Math.min(window.innerWidth * 0.36, 420);
        const expandedWidth = Math.min(window.innerWidth * 0.4, 460);
        setExpanded(bounds.width > (collapsedWidth + expandedWidth) / 2);
      } else {
        setExpanded(bounds.height > window.innerHeight * 0.45);
      }
      ignoreClickUntil = performance.now() + 500;
    }
    startPosition = null;
    startSize = null;
    dragLandscape = false;
    didDrag = false;
    sheet.style.transition = "";
    sheet.style.height = "";
    sheet.style.width = "";
  });
}
